import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  ArrowRight,
  BookOpen,
  Boxes,
  Cpu,
  Gauge,
  GitBranch,
  Joystick,
  LineChart,
  Rocket,
  Terminal,
  Zap,
} from 'lucide-react';
import { CodeBlock } from '../CodeBlock';
import {
  RL_ALGORITHMS,
  RL_CATEGORY_LABELS,
  RL_ENVIRONMENTS,
  RL_GPU_TIERS,
  RL_STARTER_REPOS,
  RLGpuTier,
  RLJob,
  RLMetricPoint,
  previewMetrics,
  rlAlgoById,
  rlEnvById,
} from './rlData';

const POLL_MS = 2000;
const PREVIEW_TICK_MS = 1200;
const PREVIEW_POINTS = 40;

const categories = Object.keys(RL_CATEGORY_LABELS);

const fmt = (v: number, digits = 2) =>
  Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : v.toFixed(digits);

const Sparkline: React.FC<{ points: RLMetricPoint[]; accessor: (p: RLMetricPoint) => number }> = ({
  points,
  accessor,
}) => {
  if (points.length < 2) {
    return <div className="h-16 rounded-md bg-white/[0.03]" />;
  }
  const vals = points.map(accessor);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const path = vals
    .map((v, i) => `${((i / (vals.length - 1)) * 100).toFixed(2)},${(30 - ((v - min) / span) * 28).toFixed(2)}`)
    .join(' ');
  return (
    <svg viewBox="0 0 100 32" preserveAspectRatio="none" className="h-16 w-full" aria-hidden>
      <polyline points={path} fill="none" stroke="rgb(56,189,248)" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const StatTile: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">{label}</p>
    <p className="mt-1 font-mono text-xl font-bold text-white">{value}</p>
    {sub ? <p className="mt-0.5 text-xs text-white/45">{sub}</p> : null}
  </div>
);

export const RLPage: React.FC = () => {
  const [envId, setEnvId] = useState('cartpole-v1');
  const [algoId, setAlgoId] = useState('ppo');
  const [gpuId, setGpuId] = useState('gpu-rtx4090');
  const [category, setCategory] = useState<string>('all');
  const [gpuTiers, setGpuTiers] = useState<RLGpuTier[]>(RL_GPU_TIERS);
  const [job, setJob] = useState<RLJob | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewTick, setPreviewTick] = useState(0);
  const [launching, setLaunching] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  const env = rlEnvById(envId) ?? RL_ENVIRONMENTS[0];
  const algo = rlAlgoById(algoId) ?? RL_ALGORITHMS[0];

  const visibleEnvs = useMemo(
    () => (category === 'all' ? RL_ENVIRONMENTS : RL_ENVIRONMENTS.filter((e) => e.category === category)),
    [category],
  );
  const compatibleAlgos = useMemo(() => RL_ALGORITHMS.filter((a) => env.algorithms.includes(a.id)), [env]);

  useEffect(() => {
    if (!env.algorithms.includes(algoId)) setAlgoId(env.algorithms[0]);
  }, [env, algoId]);

  // Live marked-up GPU pricing from the public catalog.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/rl/environments')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.hardware?.length) return;
        const tiers: RLGpuTier[] = data.hardware.map((h: any) => ({
          id: h.id,
          label: h.label,
          gpu: h.gpu,
          vramGb: h.vramGb,
          priceHourText: h.priceHourText,
          note: RL_GPU_TIERS.find((t) => t.id === h.id)?.note ?? '',
        }));
        setGpuTiers(tiers);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll a real job.
  useEffect(() => {
    if (!job || previewMode || job.status !== 'running') return;
    const t = setInterval(async () => {
      try {
        const r = await fetch(`/api/rl/jobs/${job.id}`);
        if (!r.ok) return;
        const data = await r.json();
        if (data?.job) setJob(data.job);
      } catch {
        // transient poll failure; next tick retries
      }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [job, previewMode]);

  // Signed-out preview: advance a synthetic run client-side.
  useEffect(() => {
    if (!previewMode || !job || job.status !== 'running') return;
    const t = setInterval(() => setPreviewTick((n) => n + 1), PREVIEW_TICK_MS);
    return () => clearInterval(t);
  }, [previewMode, job]);

  useEffect(() => {
    if (!previewMode || !job) return;
    const n = Math.min(previewTick + 2, PREVIEW_POINTS);
    const metrics = previewMetrics(env, job.totalSteps, n);
    setJob((prev) =>
      prev
        ? {
            ...prev,
            metrics,
            progress: n / PREVIEW_POINTS,
            step: metrics.length ? metrics[metrics.length - 1].step : 0,
            status: n >= PREVIEW_POINTS ? 'succeeded' : 'running',
          }
        : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTick, previewMode]);

  const launch = useCallback(async () => {
    setLaunching(true);
    setError('');
    setPreviewMode(false);
    try {
      const r = await fetch('/api/rl/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ envId, algoId, hardware: gpuId }),
      });
      if (r.status === 401) {
        setPreviewMode(true);
        setPreviewTick(0);
        setJob({
          id: 'preview',
          envId,
          algoId,
          hardware: gpuId,
          status: 'running',
          totalSteps: 100000,
          simulated: true,
          progress: 0,
          step: 0,
          metrics: [],
        });
      } else {
        const data = await r.json();
        if (!r.ok || !data?.job) {
          setError(data?.error || 'Could not launch training job');
        } else {
          setJob(data.job);
        }
      }
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch {
      setError('Could not reach the training API');
    } finally {
      setLaunching(false);
    }
  }, [envId, algoId, gpuId]);

  const deploy = useCallback(
    async (target: 'cog' | 'serverless') => {
      if (!job) return;
      if (previewMode) {
        setError('Sign in to deploy trained policies as endpoints.');
        return;
      }
      setDeploying(true);
      setError('');
      try {
        const r = await fetch(`/api/rl/jobs/${job.id}/deploy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ target }),
        });
        const data = await r.json();
        if (!r.ok || !data?.job) setError(data?.error || 'Deploy failed');
        else setJob(data.job);
      } catch {
        setError('Deploy request failed');
      } finally {
        setDeploying(false);
      }
    },
    [job, previewMode],
  );

  const last = job?.metrics.length ? job.metrics[job.metrics.length - 1] : null;

  return (
    <main className="overflow-hidden bg-black text-white">
      <section className="relative border-b border-white/10 px-4 pb-14 pt-24 sm:px-8 lg:px-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.12),transparent_55%)]" />
        <div className="relative mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-2 lg:items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/58">
              <Joystick size={12} className="text-sky-400" />
              Hosted RL
            </div>
            <h1 className="mt-6 max-w-[18ch] font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
              Train RL policies on cloud GPUs
            </h1>
            <p className="mt-6 max-w-xl text-base leading-8 text-white/72">
              Pick an environment — gymnasium classic control, Atari, MuJoCo, LLM RLHF, or robotics sim — a
              baseline repo, and a GPU. Metrics stream live. When the reward curve flattens, deploy the policy
              as a scale-to-zero cog or serverless endpoint.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#launch" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
                Launch a training run <ArrowRight size={16} />
              </a>
              <a href="#starters" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
                <GitBranch size={16} /> OSS starter repos
              </a>
            </div>
          </div>
          <div className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-6">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/45">
              <Terminal size={14} className="text-sky-400" /> CLI
            </p>
            <CodeBlock
              className="mt-4"
              language="bash"
              preClassName="!text-[13px] !leading-6"
              code={`app api POST /api/rl/jobs '{
  "envId": "cartpole-v1",
  "algoId": "ppo",
  "hardware": "gpu-rtx4090"
}'
app api GET  /api/rl/jobs/<job-id>          # poll metrics
app api POST /api/rl/jobs/<job-id>/deploy \\
  '{"target": "serverless"}'                # policy endpoint`}
            />
            <p className="mt-3 text-xs leading-6 text-white/45">
              Per-second GPU billing from the shared credit balance; jobs scale to zero when training
              finishes.
            </p>
          </div>
        </div>
      </section>

      <section id="launch" className="scroll-mt-20 border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1400px]">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">1 · environment</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Pick an environment</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {['all', ...categories].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                  category === c
                    ? 'border-sky-400/60 bg-sky-400/15 text-sky-300'
                    : 'border-white/10 bg-white/[0.04] text-white/60 hover:border-white/25'
                }`}
              >
                {c === 'all' ? 'All' : RL_CATEGORY_LABELS[c]}
              </button>
            ))}
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleEnvs.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => setEnvId(e.id)}
                className={`rounded-2xl border p-5 text-left transition ${
                  envId === e.id
                    ? 'border-sky-400/50 bg-sky-400/[0.08]'
                    : 'border-white/10 bg-white/[0.04] hover:border-white/25'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
                    {e.categoryLabel}
                  </span>
                  {envId === e.id ? <Zap size={14} className="text-sky-400" /> : null}
                </div>
                <h3 className="mt-3 font-display text-lg font-bold">{e.name}</h3>
                <p className="mt-1.5 text-sm leading-6 text-white/65">{e.description}</p>
                <p className="mt-3 font-mono text-[11px] text-white/45">
                  obs {e.observation} · act {e.action} · {e.algorithms.map((a) => a.toUpperCase()).join('/')}
                </p>
              </button>
            ))}
          </div>

          <p className="mt-12 text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">2 · algorithm repo</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Pick a baseline</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {RL_ALGORITHMS.map((a) => {
              const supported = compatibleAlgos.some((c) => c.id === a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  disabled={!supported}
                  onClick={() => setAlgoId(a.id)}
                  className={`rounded-2xl border p-5 text-left transition ${
                    algoId === a.id && supported
                      ? 'border-sky-400/50 bg-sky-400/[0.08]'
                      : supported
                        ? 'border-white/10 bg-white/[0.04] hover:border-white/25'
                        : 'cursor-not-allowed border-white/5 bg-white/[0.02] opacity-45'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-bold">{a.name}</h3>
                    <GitBranch size={14} className="text-sky-400" />
                  </div>
                  <p className="mt-1.5 text-sm leading-6 text-white/65">{a.description}</p>
                  <span className="mt-3 inline-block font-mono text-[11px] text-white/45">
                    {a.repo.replace('https://github.com/', 'github.com/')}
                  </span>
                  {!supported ? (
                    <p className="mt-2 text-[11px] text-white/40">not supported for {env.name}</p>
                  ) : null}
                </button>
              );
            })}
          </div>

          <p className="mt-12 text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">3 · gpu tier</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Pick a GPU</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {gpuTiers.map((g) => {
              const tooSmall = g.vramGb < env.minVramGb;
              return (
                <button
                  key={g.id}
                  type="button"
                  disabled={tooSmall}
                  onClick={() => setGpuId(g.id)}
                  className={`rounded-xl border p-4 text-left transition ${
                    gpuId === g.id && !tooSmall
                      ? 'border-sky-400/50 bg-sky-400/[0.08]'
                      : tooSmall
                        ? 'cursor-not-allowed border-white/5 bg-white/[0.02] opacity-45'
                        : 'border-white/10 bg-white/[0.04] hover:border-white/25'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-display text-base font-bold">
                      <Cpu size={14} className="text-sky-400" /> {g.label}
                    </span>
                    <span className="font-mono text-sm text-sky-300">{g.priceHourText}/hr</span>
                  </div>
                  <p className="mt-1 text-xs text-white/50">
                    {g.vramGb} GB VRAM{g.note ? ` — ${g.note}` : ''}
                    {tooSmall ? ` (needs ${env.minVramGb} GB)` : ''}
                  </p>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-white/45">
            Per-second billing with the platform margin included, the same catalog as{' '}
            <a href="/cogs" className="font-semibold text-sky-400 hover:text-sky-300">Cog Studio</a> and{' '}
            <a href="/pricing" className="font-semibold text-sky-400 hover:text-sky-300">pricing</a>.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={launch}
              disabled={launching}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-8 text-sm font-bold text-black transition hover:bg-sky-300 disabled:opacity-60"
            >
              <Rocket size={16} /> {launching ? 'Launching…' : `Train ${algo.name} on ${env.name}`}
            </button>
            {error ? <p className="text-sm text-rose-400">{error}</p> : null}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1400px]" ref={panelRef}>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">live training metrics</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Reward, loss, entropy, and throughput</h2>
          {!job ? (
            <div className="mt-8 flex items-center gap-3 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-white/55">
              <Activity size={18} className="text-sky-400" />
              <p className="text-sm leading-7">
                Launch a run above to stream reward, loss, entropy, and throughput here, polled from{' '}
                <code className="font-mono text-xs">/api/rl/jobs/&#123;id&#125;</code>. Signed-out visitors get a
                simulated run.
              </p>
            </div>
          ) : (
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-sm text-white/70">
                  {rlEnvById(job.envId)?.name ?? job.envId} · {rlAlgoById(job.algoId)?.name ?? job.algoId} ·{' '}
                  {job.hardware}
                </p>
                <span
                  className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${
                    job.status === 'succeeded'
                      ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
                      : job.status === 'failed'
                        ? 'border-rose-400/30 bg-rose-400/10 text-rose-300'
                        : 'border-sky-400/30 bg-sky-400/10 text-sky-300'
                  }`}
                >
                  {previewMode ? 'preview · ' : job.simulated ? 'simulated · ' : ''}
                  {job.status}
                </span>
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="h-full rounded-full bg-sky-400 transition-all duration-700"
                  style={{ width: `${Math.round(job.progress * 100)}%` }}
                />
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatTile label="episode reward" value={last ? fmt(last.reward) : '—'} sub={`target ${fmt(env.targetReward)}`} />
                <StatTile label="policy loss" value={last ? fmt(last.loss, 4) : '—'} />
                <StatTile label="steps" value={job.step.toLocaleString()} sub={`of ${job.totalSteps.toLocaleString()}`} />
                <StatTile
                  label="throughput"
                  value={last ? `${fmt(last.stepsPerSec, 1)}/s` : '—'}
                  sub={last ? `GPU ${fmt(last.gpuUtil, 0)}%` : undefined}
                />
              </div>
              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                  <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
                    <LineChart size={12} className="text-sky-400" /> episode reward
                  </p>
                  <Sparkline points={job.metrics} accessor={(p) => p.reward} />
                </div>
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                  <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
                    <Gauge size={12} className="text-sky-400" /> policy loss
                  </p>
                  <Sparkline points={job.metrics} accessor={(p) => p.loss} />
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-white/45">
                  <Boxes size={14} className="text-sky-400" /> deploy the trained policy
                </p>
                {job.deployment ? (
                  <div className="mt-3 text-sm leading-7 text-white/70">
                    <p>
                      Policy registered as a {job.deployment.kind} endpoint. Manage it in{' '}
                      <a href={job.deployment.studioUrl} className="font-semibold text-sky-400 hover:text-sky-300">
                        Cog Studio
                      </a>
                      .
                    </p>
                    <code className="mt-2 block overflow-x-auto rounded-md bg-black/50 px-3 py-2 font-mono text-xs text-white/70">
                      POST {job.deployment.apiUrl} {'{"input": {"observation": "[0.01, -0.02, 0.03, 0.04]"}}'}
                    </code>
                  </div>
                ) : (
                  <>
                    <p className="mt-2 text-sm leading-7 text-white/65">
                      One click packages the policy behind the cog HTTP contract (
                      <code className="font-mono text-xs">/predictions</code> on port 5000) and registers it.
                      Serverless scales to zero in seconds; a cog pod keeps warm state. See{' '}
                      <a href="/cogs" className="font-semibold text-sky-400 hover:text-sky-300">/cogs</a> and{' '}
                      <code className="font-mono text-xs">docs/BUILDING_COGS.md</code>.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => deploy('serverless')}
                        disabled={deploying || job.status !== 'succeeded'}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-sky-400 px-5 text-sm font-bold text-black transition hover:bg-sky-300 disabled:opacity-50"
                      >
                        <Zap size={14} /> Serverless endpoint
                      </button>
                      <button
                        type="button"
                        onClick={() => deploy('cog')}
                        disabled={deploying || job.status !== 'succeeded'}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-5 text-sm font-bold text-white transition hover:bg-white hover:text-black disabled:opacity-50"
                      >
                        <Boxes size={14} /> Cog endpoint
                      </button>
                      {job.status !== 'succeeded' ? (
                        <p className="self-center text-xs text-white/45">available once training completes</p>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section id="starters" className="scroll-mt-20 border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1400px]">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">open source</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Starter repos</h2>
          <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
            Every baseline here is an MIT-licensed repo. Each ships a single-file training loop, a{' '}
            <code className="font-mono text-sm">cog.yaml</code>, an app.nz policy server, and a serverless
            deploy manifest per{' '}
            <code className="font-mono text-sm">docs/BUILDING_COGS.md</code>.
          </p>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            {RL_STARTER_REPOS.map((r) => (
              <a
                key={r.name}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition hover:border-sky-400/40 hover:bg-white/[0.05]"
              >
                <div className="flex items-center justify-between">
                  <GitBranch size={20} className="text-sky-400" />
                  <span className="rounded-full border border-sky-400/25 bg-sky-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-sky-300">
                    {r.license} · {r.algorithms.map((a) => a.toUpperCase()).join(' + ')}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-xl font-bold group-hover:text-sky-300">{r.name}</h3>
                <p className="mt-2 text-sm leading-7 text-white/65">{r.description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-sky-400">
                  {r.url.replace('https://', '')} <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
                </span>
              </a>
            ))}
          </div>
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <BookOpen size={18} className="mt-0.5 shrink-0 text-sky-400" />
            <p className="text-sm leading-7 text-white/65">
              Any container that trains and then serves the cog HTTP contract can run here. Build it with{' '}
              <a href="/builds" className="font-semibold text-sky-400 hover:text-sky-300">hosted builds</a>,
              register it on <a href="/cogs" className="font-semibold text-sky-400 hover:text-sky-300">/cogs</a>, and post
              metrics to the training API.
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-8 sm:py-20 lg:px-12">
        <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center sm:px-12">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14),transparent_60%)]" />
          <div className="relative">
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-4xl">
              Train the policy, then serve it
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-8 text-white/70">
              Train on per-second billed GPUs, deploy the policy as a scale-to-zero endpoint, and call it
              over HTTP.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a href="#launch" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
                <Rocket size={16} /> Launch a run
              </a>
              <a href="/cogs" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
                <Boxes size={16} /> Cog Studio
              </a>
              <a href="/cli" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
                <Terminal size={16} /> Install the CLI
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RLPage;
